import Api from '../../api'
import { takeEvery, takeLatest, call, put } from 'redux-saga/effects'
import { LOGOUT } from 'portal-modules/auth/redux/modules/user'
import { addSettings } from 'portal-modules/common/redux/modules/settings'
import createRequestSaga from 'portal-modules/common/redux/create-request-saga'
import createFormAction from 'portal-modules/common/redux/create-form-action'
import getProjectName from 'portal-modules/common/utils/get-project-name'

// action types
export const REQUEST_INSURANCE_LIST = 'rp/insurance-tp/REQUEST_INSURANCE_LIST'
export const RECEIVE_INSURANCE_LIST = 'rp/insurance-tp/RECEIVE_INSURANCE_LIST'
export const RECEIVE_INSURANCE_COVERAGE_REQUIREMENTS = 'rp/insurance-tp/RECEIVE_INSURANCE_COVERAGE_REQUIREMENTS'
export const REQUEST_SETTINGS = 'rp/insurance-tp/REQUEST_SETTINGS'
export const SAVE_POLICY = 'rp/insurance-tp/SAVE_POLICY'
export const POLICY_SAVED = 'rp/insurance-tp/POLICY_SAVED'
export const UPDATE_POLICY = 'rp/insurance-tp/UPDATE_POLICY'
export const POLICY_UPDATED = 'rp/insurance-tp/POLICY_UPDATED'
export const DELETE_POLICY = 'rp/insurance-tp/DELETE_POLICY'
export const POLICY_DELETED = 'rp/insurance-tp/POLICY_DELETED'
export const RECEIVE_POLICY_ENROLMENT_LINK =
  'rp/insurance-tp/RECEIVE_POLICY_ENROLMENT_LINK'
export const REQUEST_ENROLLMENT_LINK = 'rp/insurance-tp/REQUEST_ENROLLMENT_LINK'
export const SUBMIT_SHOPPING_REQUEST = 'rp/insurance-tp/SUBMIT_SHOPPING_REQUEST'
export const SHOPPING_REQUEST_SUCCESS = 'rp/insurance-tp/SHOPPING_REQUEST_SUCCESS'
export const SHOPPING_REQUEST_ERROR = 'rp/insurance-tp/SHOPPING_REQUEST_ERROR'

//action creator
export const requestInsuranceList = () => ({
  type: REQUEST_INSURANCE_LIST,
})

export const saveInsurancePolicy = createFormAction(SAVE_POLICY)

export const getInsuranceSettingsList = () => ({
  type: REQUEST_SETTINGS,
})

export const getInsuranceEnrollmentLink = () => ({
  type: REQUEST_ENROLLMENT_LINK,
})

export const deletePolicy = payload => ({
  type: DELETE_POLICY,
  payload,
})

export const submitInsuranceShoppingRequest = createFormAction(SUBMIT_SHOPPING_REQUEST)

// reducer
export default (
  state = {
    policies: [],
    enrollmentLink: '',
  },
  { payload, type },
) => {
  switch (type) {
    case RECEIVE_INSURANCE_LIST:
      return {
        ...state,
        policies: payload,
      }
    case POLICY_SAVED:
      return {
        ...state,
        policies: { ...state.policies, ...payload },
      }
    case POLICY_DELETED:
      return (
        state &&
        Object.values(state.policies).reduce((obj, policy) => {
          if (policy.id === payload) return obj
          return { ...obj, [policy.id]: policy }
        }, {})
      )
    case LOGOUT:
      return {}
    case RECEIVE_POLICY_ENROLMENT_LINK:
      return {
        ...state,
        enrollmentLink: payload.enrollmentLink,
      }
    case RECEIVE_INSURANCE_COVERAGE_REQUIREMENTS:
      return {
        ...state,
        minimum_coverage_requirements: payload,
      }
    default:
      return state
  }
}

//sagas

function* getInsuranceList() {
  
  const { policies, insurance_policy_enrollment_link, minimum_coverage_requirements
  } = yield call(
    
    getProjectName() == 'tp' ? Api.getTpInsurances : Api.getInsurances,
  )
  
  if (policies?.length) {
    yield put({
      type: RECEIVE_INSURANCE_LIST,
      payload:
        policies &&
        Object.values(policies).reduce(
          (obj, { ...rest }) => ({
            ...obj,
            [rest.id]: {
              ...rest,
            },
          }),
          {},
        ),
    })
  }
  if (insurance_policy_enrollment_link) {
    yield put({
      type: RECEIVE_POLICY_ENROLMENT_LINK,
      payload: { enrollmentLink: insurance_policy_enrollment_link },
    })
  }
  if (Object.keys(minimum_coverage_requirements)?.length) {
    yield put({
      type: RECEIVE_INSURANCE_COVERAGE_REQUIREMENTS,
      payload: minimum_coverage_requirements
    })
  }
}

function* savePolicy({ payload, isTp }) {
  const formData = new FormData()

  formData.append('policy', JSON.stringify(payload.policy))

  payload.proof_of_insurance.map(image => {
    formData.append(`proof_of_insurance`, image)
  })

  if (isTp) {
    payload.additional_document_1.map(image => {
      formData.append(`additional_document_1`, image)
    })

    payload.additional_document_2.map(image => {
      formData.append(`additional_document_2`, image)
    })
  }

  const { policy } = yield call(
    isTp ? Api.saveTpInsurance : Api.saveInsurance,
    formData,
  )

  if (policy && Object.keys(policy).length) {
    yield put({
      type: POLICY_SAVED,
      payload: {
        [policy.id]: {
          ...policy,
        },
      },
    })
  }
}

function* getInsuranceSettings() {
  const { settings } = yield call(Api.getInsuranceListWithSettings)

  if (settings && Object.keys(settings).length) {
    yield put(addSettings({ insurance: settings }))
  }
}

function* getEnrollmentLink() {
  const { insurance_policy_enrollment_link } = yield call(
    getProjectName() == 'tp' ? Api.getTpInsurances : Api.getEnrollmentLink,
  )

  if (insurance_policy_enrollment_link) {
    yield put({
      type: RECEIVE_POLICY_ENROLMENT_LINK,
      payload: { enrollmentLink: insurance_policy_enrollment_link },
    })
  }
}

function* submitShoppingRequest({ payload, callback, onError }) {
  try {
    const response = yield call(Api.submitInsuranceShoppingRequest, payload)

    yield put({
      type: SHOPPING_REQUEST_SUCCESS,
      payload: response,
    })

    if (callback) {
      callback(response)
    }
  } catch (error) {
    yield put({
      type: SHOPPING_REQUEST_ERROR,
      payload: error,
    })

    if (onError) {
      onError(error)
    }
  }
}

function* deleteInsurancePolicy({ payload }) {
  yield call(Api.deletePolicy, { id: payload })
  yield put({ type: POLICY_DELETED, payload })
}

function* watchRequestInsurance() {
  yield takeLatest(
    REQUEST_INSURANCE_LIST,
    createRequestSaga(getInsuranceList, {
      key: 'insurance',
      message: __('Looking up insurance'),
    }),
  )
}

function* watchSavePolicy() {
  yield takeEvery(
    SAVE_POLICY,
    createRequestSaga(savePolicy, {
      key: 'insurance',
      message: __('Saving insurance policy'),
      successMessage: __('Insurance policy successfully saved.'),
      write: true,
    }),
  )
}

function* watchDeletePolicy() {
  yield takeEvery(
    DELETE_POLICY,
    createRequestSaga(deleteInsurancePolicy, {
      key: 'insurance',
      message: __('Deleting insurance policy'),
      successMessage: __('Insurance policy successfully deleted'),
      write: true,
    }),
  )
}

function* watchGetInsuranceSettings() {
  yield takeEvery(
    REQUEST_SETTINGS,
    createRequestSaga(getInsuranceSettings, {
      key: 'insurance',
      message: __('Looking up insurance settings'),
    }),
  )
}

function* watchGetEnrollmentLink() {
  yield takeEvery(
    REQUEST_ENROLLMENT_LINK,
    createRequestSaga(getEnrollmentLink, {
      key: 'insurance',
      message: __('Looking up insurance settings'),
    }),
  )
}

function* watchSubmitShoppingRequest() {
  yield takeEvery(
    SUBMIT_SHOPPING_REQUEST,
    createRequestSaga(submitShoppingRequest, {
      key: 'insurance',
      message: __('Submitting insurance shopping request'),
      successMessage: __('Insurance shopping request submitted successfully'),
      write: true,
    }),
  )
}

export const sagas = [
  watchRequestInsurance,
  watchSavePolicy,
  watchDeletePolicy,
  watchGetInsuranceSettings,
  watchGetEnrollmentLink,
  watchSubmitShoppingRequest,
]
